import { LogoutOutlined, MenuOutlined } from "@ant-design/icons";
import { Button, Drawer, Grid, Layout, Menu, Result, Spin, Typography } from "antd";
import { type ReactNode, useEffect, useState } from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ApiError, errorMessage } from "../api/client";
import { usePortalStore } from "./store";

/**
 * CW31-B15: a section of the cabinet the role may not open, or one that failed to load, says so in plain words with
 * the way back — a refusal is never shown as «товара нет» and is not offered «Повторить»; a failure is.
 */
export function PortalProblem({
  error,
  forbidden,
  deniedTitle,
  deniedHint,
  failedTitle,
  onRetry,
}: {
  error?: unknown;
  /** The role has no right: the page did not even ask the server. */
  forbidden?: boolean;
  deniedTitle: string;
  deniedHint?: ReactNode;
  failedTitle: string;
  onRetry?: () => void;
}) {
  const can = usePortalStore((s) => s.can);
  const denied = forbidden || (error instanceof ApiError && error.problem.status === 403);
  const detail = error ? errorMessage(error) : "Это действие недоступно вашей роли в кабинете. Обратитесь к администратору кабинета.";
  return (
    <div data-testid={denied ? "portal-denied" : "portal-load-failed"}>
      <Result
        status={denied ? "403" : "warning"}
        title={denied ? deniedTitle : failedTitle}
        subTitle={
          <>
            <div>{detail}</div>
            {denied && deniedHint ? <div style={{ marginTop: 6 }}>{deniedHint}</div> : null}
          </>
        }
        extra={[
          !denied && onRetry ? (
            <Button key="retry" type="primary" onClick={onRetry}>
              Повторить
            </Button>
          ) : null,
          // UXW-04: the start page of the cabinet is «Рабочий стол»; the address stays /portal
          <Link key="home" to="/portal">
            <Button type={denied || !onRetry ? "primary" : "default"}>На рабочий стол</Button>
          </Link>,
          denied && can("portal.finance.read") ? (
            <Link key="finance" to="/portal/finance">
              <Button>Документы и счета</Button>
            </Link>
          ) : null,
        ]}
      />
    </div>
  );
}

const { Header, Content } = Layout;

// UXW-04 (ТЗ «Рабочие столы» 31.10.2026): «Обзор» is called «Рабочий стол», the route /portal is the same.
// «Настройки» (UXW-10, 6.1) is added after «Пароль» and only with «use_personal_workspace»; «Пароль» stays.
const ITEMS: { key: string; label: string; permission: string; option?: string }[] = [
  { key: "/portal", label: "Рабочий стол", permission: "portal.access" },
  { key: "/portal/requests", label: "Заявки", permission: "portal.requests.read" },
  { key: "/portal/operations", label: "Операции", permission: "portal.operations.read" },
  { key: "/portal/stock", label: "Остатки", permission: "portal.stock.read" },
  { key: "/portal/finance", label: "Документы и счета", permission: "portal.finance.read" },
  { key: "/portal/password", label: "Пароль", permission: "portal.access" },
  { key: "/portal/settings", label: "Настройки", permission: "portal.access", option: "use_personal_workspace" },
];

export function RequirePortalAuth() {
  const token = usePortalStore((s) => s.accessToken);
  const location = useLocation();
  if (!token) return <Navigate to="/portal/login" replace state={{ from: location }} />;
  return <Outlet />;
}

/**
 * ТЗ 1.2 NAV-05 / C01–C06: the external cabinet has its own menu by its own rights; it never touches the staff menu.
 * Large text and tall controls (4.2) come from the shared theme with the «large» density.
 */
export function PortalLayout() {
  const { me, loadMe, logout, can } = usePortalStore();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = Grid.useBreakpoint();
  const narrow = screens.md === false;
  const [open, setOpen] = useState(false);
  useEffect(() => {
    loadMe().catch((e) => logout(e instanceof Error ? e.message : undefined));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => setOpen(false), [location.pathname]);
  if (!me) return <Spin fullscreen />;
  if (me.must_change_password && location.pathname !== "/portal/password") return <Navigate to="/portal/password" replace />;
  const items = ITEMS.filter((item) => can(item.permission) && (!item.option || me.feature_options?.[item.option])).map((item) => ({
    key: item.key,
    label: (
      <Link to={item.key} data-testid={`portal-nav-${item.key.split("/")[2] ?? "home"}`}>
        {item.label}
      </Link>
    ),
  }));
  const selected = ITEMS.map((i) => i.key).filter((key) => (key === "/portal" ? location.pathname === "/portal" : location.pathname.startsWith(key)));
  const menu = (mode: "horizontal" | "inline") => <Menu mode={mode} items={items} selectedKeys={selected} style={{ borderBottom: 0, background: "transparent", minWidth: 0, flex: 1 }} />;
  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Header style={{ display: "flex", alignItems: "center", gap: 16, background: "var(--wms-surface)", borderBottom: "1px solid var(--wms-border)", paddingInline: narrow ? 12 : 24 }}>
        {narrow && <Button icon={<MenuOutlined />} onClick={() => setOpen(true)} aria-label="Открыть меню" />}
        <Link to="/portal" style={{ color: "inherit", display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
          <span style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg, #2f6fed, #7c3aed)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700 }}>K</span>
          <span style={{ lineHeight: 1.1 }}>
            <span style={{ display: "block", fontWeight: 700 }}>Кабинет клиента</span>
            <span style={{ display: "block", fontSize: 12, color: "var(--wms-muted)" }}>{me.partner.name}</span>
          </span>
        </Link>
        {!narrow && menu("horizontal")}
        <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          {/* UXW-06: with «Настройки» the menu needs the room of the name on a laptop screen; the name moves to the
              hint of «Выйти» there instead of the menu items folding into «…» */}
          {!narrow && (items.length <= 6 || screens.xxl) && <Typography.Text type="secondary">{me.user.full_name}</Typography.Text>}
          <Button icon={<LogoutOutlined />} onClick={() => { logout(); navigate("/portal/login"); }} data-testid="portal-logout" title={`${me.user.full_name}: выйти`}>
            {narrow ? null : "Выйти"}
          </Button>
        </span>
      </Header>
      {narrow && (
        <Drawer open={open} onClose={() => setOpen(false)} placement="left" width={280} title={me.partner.name}>
          {menu("inline")}
        </Drawer>
      )}
      <Content style={{ padding: narrow ? "16px 16px 40px" : "22px 24px 40px", maxWidth: 1200, width: "100%", margin: "0 auto", boxSizing: "border-box" }}>
        <Outlet />
      </Content>
    </Layout>
  );
}
